import React from 'react';
import PropTypes from 'prop-types';
import { Storage } from 'aws-amplify';
import { Avatar, Spin, message } from 'antd';
import Text from 'antd/lib/typography/Text';

class AvatarS3 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ready: false,
      url: '',
      error: false,
    };
    this._isMounted = false;
    this.fetchController = null;
    this.fetchPromise = null;
  }

  fetchImage = async (imgKey, placeholderSrc) => {
    if (this.fetchController) {
      this.fetchController.abort();
    }

    if (!imgKey) {
      if (this._isMounted) {
        this.setState({
          ready: true,
          url: placeholderSrc || '',
        });
      }
      return;
    }

    this.fetchController = new AbortController();

    try {
      const cacheKey = `avatar_${imgKey}`;
      const cachedImage = sessionStorage.getItem(cacheKey);

      if (cachedImage) {
        if (this._isMounted) {
          this.setState({
            ready: true,
            url: cachedImage,
            error: false,
          });
        }
        return;
      }

      this.fetchPromise = Storage.get(imgKey, {
        expires: 24 * 60 * 60,
        validateObjectExistence: true,
      });

      const result = await this.fetchPromise;

      try {
        sessionStorage.setItem(cacheKey, result);
      } catch (cacheErr) {
        message.error('Unable to cache avatar image');
      }

      if (this._isMounted) {
        this.setState({
          ready: true,
          url: result,
          error: false,
        });
      }
    } catch (err) {
      if (!err.name || err.name !== 'AbortError') {
        message.error('Failed to load avatar image');
      }

      if (this._isMounted) {
        this.setState({
          ready: true,
          error: true,
          url: placeholderSrc || '',
        });
      }
    }
  };

  handleImageError = () => {
    const { imgKey, placeholderSrc } = this.props;
    if (this._isMounted) {
      this.setState({
        error: true,
        url: placeholderSrc || '',
      });
      // Clear cached URL if exists
      if (imgKey) {
        const cacheKey = `avatar_${imgKey}`;
        sessionStorage.removeItem(cacheKey);
      }
    }
  };

  componentDidMount() {
    this._isMounted = true;
    const { imgKey, placeholderSrc } = this.props;
    this.fetchImage(imgKey, placeholderSrc);
  }

  componentDidUpdate(prevProps) {
    const { imgKey, placeholderSrc } = this.props;
    if (imgKey !== prevProps.imgKey) {
      this.setState({ ready: false, error: false });
      this.fetchImage(imgKey, placeholderSrc);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.fetchController) {
      this.fetchController.abort();
    }
  }

  render() {
    const { avatarProps, username } = this.props;
    const { ready, url, error } = this.state;

    if (!ready) {
      return <Spin size="small" />;
    }

    if (url && !error) {
      return <Avatar {...avatarProps} src={url} onError={this.handleImageError} />;
    }

    if (username) {
      return (
        <Avatar
          {...avatarProps}
          style={{
            backgroundColor: '#e5e7eb',
            color: 'black',
            fontSize: '15px',
            outline: '2px solid white',
          }}
        >
          <Text strong>{username.substring(0, 1).toUpperCase()}</Text>
        </Avatar>
      );
    }

    return <Avatar {...avatarProps} />;
  }
}

AvatarS3.propTypes = {
  imgKey: PropTypes.string,
  avatarProps: PropTypes.object,
  placeholderSrc: PropTypes.string,
  username: PropTypes.string,
};

AvatarS3.defaultProps = {
  placeholderSrc: '',
  username: '',
};

export default AvatarS3;
